// teamName.ts

/**
 * Extract a clean Team Name from a banner label.
 * - Removes digits
 * - Removes words: soccer, banner, baseball, softball (singular/plural)
 * - Removes special characters, keeps letters and spaces
 * - Collapses extra spaces and trims
 * - Title-cases the result (e.g., "blue vipers" -> "Blue Vipers")
 */
export function getTeamName(label: string): string {
    if (!label) return "";

    // 1) Normalize whitespace and separators first
    let s = label
        // unify common separators to space
        .replace(/[_\-–—/:|.,+(){}\[\]\\]+/g, " ")
        // collapse multiple spaces
        .replace(/\s+/g, " ")
        .trim();

    // 2) Remove digits
    s = s.replace(/\d+/g, " ");

    // 3) Remove stopwords (soccer, banner, baseball, softball) – singular & plural
    //    \b word boundary, i modifier for case-insensitive
    s = s.replace(/\b(soccer|banners?|baseballs?|softballs?)\b/gi, " ");

    // 4) Remove any non-letter characters (keep spaces only)
    //    This strips accents too; if you want to preserve accents, switch to \p{L} with Unicode flag.
    s = s.replace(/[^A-Za-z\s]/g, " ");

    // 5) Collapse spaces again and trim
    s = s.replace(/\s+/g, " ").trim();

    // 6) Title-case
    s = titleCase(s);

    return s;
}

/**
 * Title-case each word (simple English rules).
 */
function titleCase(input: string): string {
    if (!input) return "";
    return input
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
}

/* =========================
   Examples
   =========================
getTeamName("Torpedoes 2 Soccer Banner")         // "Torpedoes"
getTeamName("AYSO Blue Vipers Soccer Banners")   // "Ayso Blue Vipers"
getTeamName("Softball – Lion Kings 2025")        // "Lion Kings"
getTeamName("BASEBALL: Thunder-Bolts #12")       // "Thunder Bolts"
getTeamName("Sharks_11x – Custom Banner")        // "Sharks"
*/
export default getTeamName;